"use client";

import React, { createContext, useContext, useState } from "react";
import axios from "axios";
import { useCart } from "./CartContext";
import { BASE_URL } from "../config/apiconfig";

const PromoCodeCheckContext = createContext();

export { PromoCodeCheckContext };

export const PromoCodeCheckProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [appliedPromoCode, setAppliedPromoCode] = useState(null);
  const [promoDiscount, setPromoDiscount] = useState(0);

  const { cart } = useCart();

  const checkPromoCode = async (code, orderValue) => {
    setLoading(true);
    setError(null);

    try {
      // Get user_id from localStorage
      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.id;

      if (!userId) {
        setError("User not found. Please login again.");
        return {
          success: false,
          message: "User not found. Please login again.",
        };
      }


      const response = await axios.post(
        `${BASE_URL}/promo-code/check`,
        {
          code: code,
          user_id: userId,
          order_value: orderValue,
        },
      );


      if (response.data.status === "200") {
        const { product_id, value } = response.data.data;


        // Parse product_id (can be comma-separated string)
        const eligibleProductIds = product_id
          ? product_id
              .split(",")
              .map((id) => parseInt(id.trim()))
              .filter((id) => !isNaN(id))
          : [];


        // Check if any cart item matches the promo code criteria
        let isPromoValid = false;
        let matchedItems = [];

        // Check each cart item
        for (const cartItem of cart) {
          const itemId = parseInt(cartItem.id);


          // Check if item matches by product ID
          if (
            eligibleProductIds.length > 0 &&
            eligibleProductIds.includes(itemId)
          ) {
            isPromoValid = true;
            matchedItems.push({
              type: "product",
              id: itemId,
              name: cartItem.name,
            });
          }
        }


        if (isPromoValid) {
          // Success - at least one product matches
          const discountValue = parseFloat(value);
          setAppliedPromoCode({
            code: code,
            productIds: eligibleProductIds,
            discount: discountValue,
            matchedItems: matchedItems,
          });
          setPromoDiscount(discountValue);


          return {
            success: true,
            message: "Promo code applied successfully!",
            discount: discountValue,
            code: code,
            productIds: eligibleProductIds,
            matchedItems: matchedItems,
          };
        } else {
          // No matching products found

          setError("This promo code is not applicable to items in your cart");
          return {
            success: false,
            message: "This promo code is not applicable to items in your cart",
          };
        }
      }
    } catch (err) {
      let errorMessage = "Failed to validate promo code";

      if (err.response?.data) {
        // Handle specific error messages from API
        if (err.response.data.message === "Minimum order value not met") {
          errorMessage = "Minimum order value not met for this promo code";
        } else if (
          err.response.data.message === "Promo code expired or not active"
        ) {
          errorMessage = "Promo code has expired or is not active";
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.status === "404") {
          errorMessage = "Invalid promo code";
        }
      }

      setError(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromoCode(null);
    setPromoDiscount(0);
    setError(null);
  };

  const clearPromoState = () => {
    setAppliedPromoCode(null);
    setPromoDiscount(0);
    setError(null);
    setLoading(false);
  };

  return (
    <PromoCodeCheckContext.Provider
      value={{
        loading,
        error,
        appliedPromoCode,
        promoDiscount,
        checkPromoCode,
        removePromoCode,
        clearPromoState,
      }}
    >
      {children}
    </PromoCodeCheckContext.Provider>
  );
};

export const usePromoCodeCheck = () => {
  const context = useContext(PromoCodeCheckContext);
  if (!context) {
    throw new Error(
      "usePromoCodeCheck must be used within a PromoCodeCheckProvider",
    );
  }
  return context;
};
